import { z } from 'zod';
import { TypeOfAPIError, TypeOfAPIResponse } from '@ninja/api-client';
import { APIFactoryOptions, factory } from '../../../config/apiClientConfig';

const planSchema = z.object({
	id: z.number(),
	name: z.string(),
	topHospitals: z.string(),
	code: z.string(),
	insuranceCompanyId: z.number(),
	planTypeId: z.number(),
	annualLimitCover: z.string(),
	annualLimitPharmacy: z.string(),
	coPaymentsDiagnostic: z.string(),
	coPaymentsPharmacy: z.string(),
	coPaymentsLabTests: z.string(),
	coPaymentsOPConsultations: z.string(),
	coPaymentsPhysioTreatment: z.string(),
	coPaymentsMaternity: z.string(),
	coPaymentsDental: z.string(),
	coPaymentsOptical: z.string(),
	policyWordingUrlEn: z.string(),
	policyWordingUrlAr: z.string(),
	planCategoryId: z.number().optional(),
});

const memberDocumentSchema = z.object({
	id: z.number(),
	referenceNumber: z.string(),
	documentType: z.number(),
	cdnUrl: z.string(),
	filePath: z.string(),
	isOnAmazonS3: z.boolean(),
	createdOn: z.string(),
	updatedOn: z.string(),
	hideFromCustomer: z.boolean(),
});

const memberSchema = z.object({
	dateOfBirth: z.string().optional(),
	genderId: z.number().optional(),
	relationshipTypeId: z.number().optional(),
	priceWithVat: z.number().optional(),
	priceWithoutVat: z.number().optional(),
	bmiLoading: z.number().optional(),
	vatOnBMILoading: z.number().optional(),
	mafDocumentUrl: z.string().optional(),
	medicalCardUrl: z.string().optional(),
	nationalityId: z.number().optional(),
	memberId: z.string().optional(),
	firstName: z.string().optional(),
	lastName: z.string().optional(),
	documents: z.array(memberDocumentSchema).optional(),
	weight: z.number().optional(),
	height: z.number().optional(),
	emiratesIdNumber: z.number().optional(),
	unifiedNumber: z.string().optional(),
	visaFileNumber: z.string().optional(),
	passportNo: z.string().optional(),
	occupation: z.string().optional(),
	isPrincipal: z.boolean().optional(),
});

const planKeyBenefitSchema = z.object({
	benefitCategoryId: z.number(),
	isKeyBenefit: z.boolean(),
});

const planBenefitSchema = z.object({
	benefitId: z.number(),
});

const planExclusionMappingSchema = z.object({
	planExclusionId: z.number(),
});

const planNetworkCountSchema = z.object({
	facilityTypeId: z.number(),
	count: z.number(),
	display: z.boolean(),
});

const dashboardPolicyStatusSchema = z.object({
	displayName: z.string(),
	isActive: z.boolean(),
	policyStatusDisplayNameId: z.number(),
	seq: z.number(),
});

const responseSchema = z.object({
	policyNumber: z.string(),
	startDate: z.string(),
	expiryDate: z.string(),
	issueDate: z.string(),
	insuranceCompanyId: z.number(),
	totalPolicyPremium: z.number(),
	policyWordingUrlEn: z.string(),
	policyWordingUrlAr: z.string(),
	policyCertificateUrl: z.string().optional(),
	documentReviewStatus: z.number().optional(),
	policyReceiptUrl: z.string().optional(),
	policyInvoiceUrl: z.string(),
	plan: planSchema,
	members: z.array(memberSchema),
	planKeyBenefits: z.array(planKeyBenefitSchema),
	planBenefits: z.array(planBenefitSchema),
	planExclusionMappings: z.array(planExclusionMappingSchema),
	planNetworkCounts: z.array(planNetworkCountSchema),
	sponsorName: z.string(),
	networkGroupId: z.number(),
	quoteReferenceNumber: z.string(),
	policyType: z.number(),
	emirateId: z.number(),
	sponsorSalaryBandId: z.number(),
	domesticWorkerSalaryBandId: z.number().optional(),
	customerEmail: z.string(),
	premiumExcludingVAT: z.number(),
	vatOnPolicyPremium: z.number(),
	totalPremiumIncludingVAT: z.number(),
	basmahFees: z.number(),
	vatOnBasmahFees: z.number(),
	discountIncludingVAT: z.number(),
	basmahFeesIncludingVAT: z.number(),
	additionalFeesExcludingVAT: z.number(),
	additionalFeesIncludingVAT: z.number(),
	vatOnAdditionalFees: z.number(),
	totalAnnualPremium: z.number(),
	totalGrossPremium: z.number(),
	icpFeesExclVAT: z.number(),
	vatAmountOnICPFees: z.number(),
	icpFeesInclVAT: z.number(),
	purchaseDate: z.string(),
	policyStatusId: z.number(),
	tpaSubmissionDate: z.string(),
	dashboardPolicyStatusList: z.array(dashboardPolicyStatusSchema),
});

const getPolicyDetailsTab = (options: APIFactoryOptions) =>
	factory(options).createAPIDefinition({
		url: '/admin/policy',
		method: 'GET',
		responseSchema,
		requestParameters: { encryptedPolicyId: ['required', 'string'] },
	});

export default getPolicyDetailsTab;

export type GetPolicyDetailsTabAPIResponse = TypeOfAPIResponse<ReturnType<typeof getPolicyDetailsTab>>;

export type GetPolicyDetailsTabError = TypeOfAPIError<ReturnType<typeof getPolicyDetailsTab>>;
